/* Acquisti — Log Invio Email Ordini.
 * Mostra storico invii con stato, anteprima allegato, riapertura per nuovo invio.
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch { return "—"; }
};

const STATUS_BADGE = {
  sent:  { bg: "#1b5e20", color: "white", txt: "✓ INVIATA" },
  mock:  { bg: "#ef6c00", color: "white", txt: "⚠ MOCK" },
  error: { bg: "#c62828", color: "white", txt: "✗ ERRORE" },
};

export default function AcquistiEmailLogPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [viewing, setViewing] = useState(null); // log item

  const carica = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStatus) params.status = filterStatus;
      if (search.trim()) params.search = search.trim();
      const r = await api.get("/acquisti/email-log", { params });
      setItems(r.data?.items || []);
    } catch (e) {
      alert(e.response?.data?.detail || "Errore caricamento log");
    } finally { setLoading(false); }
  };
  useEffect(() => { carica(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [filterStatus]);

  const totals = useMemo(() => {
    const out = { tot: items.length, sent: 0, mock: 0, error: 0 };
    items.forEach((it) => { if (out[it.status] !== undefined) out[it.status]++; });
    return out;
  }, [items]);

  return (
    <div data-testid="acq-email-log-page" style={{ padding: 16 }}>
      <div style={{ background: "linear-gradient(90deg,#0d47a1,#1565c0)", color: "white", padding: "12px 18px", fontSize: 14, fontWeight: 800, letterSpacing: 0.5, borderRadius: 4, marginBottom: 12 }}>
        📬 LOG INVIO ORDINI EMAIL
        <span style={{ fontSize: 11, fontWeight: 400, opacity: 0.9, marginLeft: 12 }}>
          {totals.tot} invii · {totals.sent} inviate · {totals.mock} mock · {totals.error} errori
        </span>
      </div>

      <div style={{ background: "#f5f5f5", padding: 10, marginBottom: 8, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", borderRadius: 4 }}>
        <input type="text" placeholder="🔍 Cerca fornitore, oggetto, destinatario..."
               value={search} onChange={(e) => setSearch(e.target.value)}
               onKeyDown={(e) => { if (e.key === "Enter") carica(); }}
               data-testid="ael-search"
               style={{ flex: "1 1 280px", padding: "6px 10px", border: "1px solid #bbb", fontSize: 12, borderRadius: 3 }} />
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} data-testid="ael-status"
                style={{ padding: "6px 10px", fontSize: 12, border: "1px solid #bbb", borderRadius: 3 }}>
          <option value="">Tutti gli stati</option>
          <option value="sent">✓ Inviate</option>
          <option value="mock">⚠ Mock</option>
          <option value="error">✗ Errori</option>
        </select>
        <button onClick={carica} disabled={loading}
                style={{ padding: "6px 12px", background: "#1565c0", color: "white", border: "none", fontSize: 11, fontWeight: 700, cursor: "pointer", borderRadius: 3 }}>
          {loading ? "…" : "🔄"}
        </button>
      </div>

      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", fontSize: 11, borderCollapse: "collapse", background: "white" }}>
          <thead>
            <tr style={{ background: "#0d47a1", color: "white" }}>
              <th style={{ textAlign: "center", padding: "6px 8px", width: 110 }}>QUANDO</th>
              <th style={{ textAlign: "center", padding: "6px 8px", width: 70 }}>NR ORD</th>
              <th style={{ textAlign: "left", padding: "6px 8px" }}>FORNITORE</th>
              <th style={{ textAlign: "left", padding: "6px 8px" }}>DESTINATARI</th>
              <th style={{ textAlign: "left", padding: "6px 8px" }}>OGGETTO</th>
              <th style={{ textAlign: "center", padding: "6px 8px", width: 90 }}>STATO</th>
              <th style={{ textAlign: "center", padding: "6px 8px", width: 130 }}>AZIONI</th>
            </tr>
          </thead>
          <tbody>
            {loading && (<tr><td colSpan={7} style={{ padding: 20, textAlign: "center", color: "#888" }}>Caricamento…</td></tr>)}
            {!loading && items.length === 0 && (
              <tr><td colSpan={7} style={{ padding: 20, textAlign: "center", color: "#888", fontStyle: "italic" }}>Nessun invio registrato.</td></tr>
            )}
            {!loading && items.map((it, i) => {
              const b = STATUS_BADGE[it.status] || STATUS_BADGE.mock;
              return (
                <tr key={it.id} data-testid={`ael-row-${it.id}`} style={{ borderTop: "1px solid #eee", background: i % 2 === 0 ? "white" : "#fafafa" }}>
                  <td style={{ padding: "5px 8px", textAlign: "center", fontFamily: "monospace", fontSize: 10 }}>
                    {fmtDateTime(it.sent_at)}
                    {it.sent_by && <div style={{ fontSize: 9, color: "#888" }}>{it.sent_by}</div>}
                  </td>
                  <td style={{ padding: "5px 8px", textAlign: "center", fontFamily: "monospace", fontWeight: 700, color: "#311b92" }}>{it.nr_documento ?? "—"}</td>
                  <td style={{ padding: "5px 8px" }}>{it.fornitore_ragione_sociale || <span style={{ color: "#bbb" }}>—</span>}</td>
                  <td style={{ padding: "5px 8px", fontSize: 10 }}>
                    {(it.to || []).map((t, idx) => <div key={idx}>{t}</div>)}
                    {(it.cc || []).length > 0 && <div style={{ color: "#666", fontStyle: "italic" }}>cc: {(it.cc || []).join(", ")}</div>}
                  </td>
                  <td style={{ padding: "5px 8px" }}>{it.subject}</td>
                  <td style={{ padding: "5px 8px", textAlign: "center" }}>
                    <span style={{ padding: "2px 8px", background: b.bg, color: b.color, fontSize: 10, fontWeight: 800, borderRadius: 2 }}>{b.txt}</span>
                    {it.error && <div style={{ fontSize: 9, color: "#c62828", marginTop: 2 }} title={it.error}>{(it.error || "").slice(0, 30)}…</div>}
                  </td>
                  <td style={{ padding: "5px 8px", textAlign: "center" }}>
                    <button onClick={() => setViewing(it)} data-testid={`ael-view-${it.id}`}
                            title="Vedi testo email" style={{ padding: "3px 8px", background: "#1565c0", color: "white", border: "none", fontSize: 10, fontWeight: 700, cursor: "pointer", marginRight: 3, borderRadius: 2 }}>📧</button>
                    {it.has_pdf && (
                      <button onClick={() => {
                        const API = process.env.REACT_APP_BACKEND_URL;
                        window.open(`${API}/api/acquisti/email-log/${it.id}/pdf`, "_blank");
                      }} data-testid={`ael-pdf-${it.id}`}
                              title="Apri allegato" style={{ padding: "3px 8px", background: "#6a1b9a", color: "white", border: "none", fontSize: 10, fontWeight: 700, cursor: "pointer", borderRadius: 2 }}>📎</button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {viewing && (<LogBodyModal log={viewing} onClose={() => setViewing(null)} />)}
    </div>
  );
}

function LogBodyModal({ log, onClose }) {
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 800, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div onClick={(e) => e.stopPropagation()} data-testid="ael-body-modal"
           style={{ background: "white", width: "100%", maxWidth: 720, maxHeight: "90vh", overflow: "auto", borderRadius: 6 }}>
        <div style={{ background: "#1565c0", color: "white", padding: "12px 18px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontWeight: 800, fontSize: 14 }}>📧 {log.subject}</div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "white", fontSize: 22, cursor: "pointer" }}>✕</button>
        </div>
        <div style={{ padding: 16 }}>
          <div style={{ fontSize: 11, color: "#555", marginBottom: 10 }}>
            <div><b>A:</b> {(log.to || []).join(", ")}</div>
            {(log.cc || []).length > 0 && <div><b>CC:</b> {(log.cc || []).join(", ")}</div>}
            <div><b>Inviata:</b> {fmtDateTime(log.sent_at)} da <b>{log.sent_by}</b></div>
            <div><b>Stato:</b> {log.status} · provider: {log.provider}</div>
          </div>
          <LogBodyContent logId={log.id} />
        </div>
      </div>
    </div>
  );
}

function LogBodyContent({ logId }) {
  const [body, setBody] = useState("Caricamento…");
  useEffect(() => {
    api.get(`/acquisti/email-log/${logId}/body`)
      .then((r) => setBody(r.data?.body || "(corpo vuoto)"))
      .catch((e) => setBody("Errore: " + (e.response?.data?.detail || "")));
  }, [logId]);
  return (
    <pre style={{ background: "#fafafa", border: "1px solid #ddd", padding: 12, fontSize: 12, fontFamily: "inherit", whiteSpace: "pre-wrap", lineHeight: 1.55, borderRadius: 3 }}>
      {body}
    </pre>
  );
}
